import * as React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

export type MarkerArrowTone = 'ink' | 'teal' | 'coral' | 'race';

const toneHex: Record<MarkerArrowTone, string> = {
  ink:   'var(--fd-text)',
  teal:  'var(--fd-teal)',
  coral: 'var(--fd-coral)',
  race:  'var(--fd-accent)',
};

export interface MarkerArrowProps extends React.SVGAttributes<SVGSVGElement> {
  tone?: MarkerArrowTone;
  /** Direction the arrow swoops. */
  direction?: 'down-right' | 'down-left' | 'right' | 'left' | 'up-right' | 'up-left';
  strokeWidth?: number;
  /** Animation duration ms. Default 900. */
  duration?: number;
  /** Delay before draw-on begins (ms). Default 200. */
  delay?: number;
  /** Animate in viewport (Framer Motion whileInView). Default true. */
  animate?: boolean;
}

const PATHS: Record<NonNullable<MarkerArrowProps['direction']>, { curve: string; head: string; viewBox: string }> = {
  'down-right': {
    viewBox: '0 0 96 80',
    curve:   'M 6 10 C 28 22, 56 40, 86 68',
    head:    'M 70 56 L 88 70 L 76 78',
  },
  'down-left': {
    viewBox: '0 0 96 80',
    curve:   'M 90 10 C 68 22, 40 40, 10 68',
    head:    'M 26 56 L 8 70 L 20 78',
  },
  'right': {
    viewBox: '0 0 96 32',
    curve:   'M 6 16 C 32 8, 64 24, 90 16',
    head:    'M 78 6 L 92 16 L 78 26',
  },
  'left': {
    viewBox: '0 0 96 32',
    curve:   'M 90 16 C 64 8, 32 24, 6 16',
    head:    'M 18 6 L 4 16 L 18 26',
  },
  'up-right': {
    viewBox: '0 0 96 80',
    curve:   'M 6 70 C 28 58, 56 40, 86 12',
    head:    'M 70 24 L 88 10 L 76 2',
  },
  'up-left': {
    viewBox: '0 0 96 80',
    curve:   'M 90 70 C 68 58, 40 40, 10 12',
    head:    'M 26 24 L 8 10 L 20 2',
  },
};

/**
 * MarkerArrow — hand-drawn marker arrow that draws on when in viewport.
 * Uses Framer Motion's whileInView for the IntersectionObserver-based trigger
 * (matches the live-workshop animation pattern from the v5 mockup).
 *
 * Example:
 *   <MarkerArrow tone="teal" direction="down-right" className="w-24 h-20" />
 */
export const MarkerArrow = React.forwardRef<SVGSVGElement, MarkerArrowProps>(
  ({
    tone = 'ink',
    direction = 'down-right',
    strokeWidth = 2.5,
    duration = 900,
    delay = 200,
    animate = true,
    className,
    style,
    ...rest
  }, ref) => {
    const { viewBox, curve, head } = PATHS[direction];
    const baseProps = {
      stroke: toneHex[tone],
      strokeWidth,
      fill: 'none',
      strokeLinecap: 'round' as const,
      strokeLinejoin: 'round' as const,
      vectorEffect: 'non-scaling-stroke' as const,
    };
    const animProps = animate
      ? {
          initial: { pathLength: 0 },
          whileInView: { pathLength: 1 },
          viewport: { once: true, margin: '-10%' },
        }
      : { initial: { pathLength: 1 }, animate: { pathLength: 1 } };

    return (
      <svg
        ref={ref}
        viewBox={viewBox}
        className={cn('block', className)}
        style={style}
        aria-hidden="true"
        {...rest}
      >
        <motion.path
          d={curve}
          {...baseProps}
          {...animProps}
          transition={{ duration: duration / 1000, delay: delay / 1000, ease: [0.45, 0.05, 0.55, 0.95] }}
        />
        <motion.path
          d={head}
          {...baseProps}
          {...animProps}
          transition={{ duration: duration / 1000 * 0.6, delay: (delay + duration * 0.7) / 1000, ease: [0.45, 0.05, 0.55, 0.95] }}
        />
      </svg>
    );
  },
);
MarkerArrow.displayName = 'MarkerArrow';
